import Fuse, { type IFuseOptions } from "fuse.js";
import {
  voteForIntegration,
  requestNewIntegration,
} from "@/actions/integrations/marketplace-actions";
import {
  IntegrationListingStatus,
  IntegrationTag,
  MARKETPLACE_FILTER_ALL,
  MARKETPLACE_FILTER_FEATURED,
  type MarketplaceFilter,
  UnifiedIntegration,
  VoteUrgency,
} from "@/types/integrations";
import {
  matchesFilter,
  normalize,
  normalizeAlphaNum,
  resolveTag,
} from "@/utils/integrations/marketplace-config";

export interface CategoryItem {
  id: MarketplaceFilter;
  labelKey: string;
  icon: string;
}

export const CATEGORIES: CategoryItem[] = [
  { id: MARKETPLACE_FILTER_FEATURED, labelKey: "marketplaceFeaturedApps", icon: "star" },
  { id: MARKETPLACE_FILTER_ALL, labelKey: "marketplaceAll", icon: "grid-2" },
  { id: IntegrationTag.AgencySystems, labelKey: "marketplaceAgencySystems", icon: "building" },
  { id: IntegrationTag.Calendar, labelKey: "marketplaceCalendar", icon: "calendar" },
  {
    id: IntegrationTag.ClientIntakeForms,
    labelKey: "marketplaceClientIntakeForms",
    icon: "file-lines",
  },
  { id: IntegrationTag.Telephony, labelKey: "marketplaceCommunications", icon: "phone" },
  { id: IntegrationTag.Crm, labelKey: "marketplaceCrm", icon: "users" },
  { id: IntegrationTag.Developer, labelKey: "marketplaceDevelopers", icon: "code" },
  { id: IntegrationTag.Marketing, labelKey: "marketplaceMarketingAutomation", icon: "megaphone" },
  { id: IntegrationTag.QuotingRaters, labelKey: "marketplaceQuotingRaters", icon: "calculator" },
  { id: IntegrationTag.ReportingBi, labelKey: "marketplaceReportingBi", icon: "chart-bar" },
  { id: IntegrationTag.TeamChat, labelKey: "marketplaceTeamChat", icon: "comments" },
];

const VISIBLE_FILTER_IDS: Set<MarketplaceFilter> = new Set(
  CATEGORIES.map((c) => c.id).filter(
    (id) => id !== MARKETPLACE_FILTER_ALL && id !== MARKETPLACE_FILTER_FEATURED
  )
);

export function isVisibleFilter(filter: MarketplaceFilter): boolean {
  return VISIBLE_FILTER_IDS.has(filter);
}

export function hasAnyVisibleTag(integration: UnifiedIntegration): boolean {
  return integration.tags.some((tag) => VISIBLE_FILTER_IDS.has(resolveTag(tag)));
}

interface ConnectedIntegrationEntry {
  id?: string;
  name?: string;
  integration?: string;
}

interface AgencyConnectionEntry {
  integration?: string;
}

export function buildConnectedData(
  connected: ConnectedIntegrationEntry[],
  agency: AgencyConnectionEntry[]
): { set: Set<string>; map: Map<string, string> } {
  const set = new Set<string>();
  const map = new Map<string, string>();
  for (const c of connected) {
    if (c.name) {
      set.add(normalize(c.name));
      if (c.id) map.set(normalizeAlphaNum(c.name), c.id);
    }
    if (c.integration) {
      set.add(normalize(c.integration));
      if (c.id) map.set(normalizeAlphaNum(c.integration), c.id);
    }
  }
  for (const c of agency) {
    if (c.integration) {
      set.add(normalize(c.integration));
      set.add(normalizeAlphaNum(c.integration));
    }
  }
  return { set, map };
}

const FUSE_OPTIONS: IFuseOptions<UnifiedIntegration> = {
  keys: [
    { name: "name", weight: 2 },
    { name: "description", weight: 1 },
    { name: "category", weight: 0.5 },
    { name: "tags", weight: 0.5 },
    { name: "key", weight: 1.5 },
  ],
  threshold: 0.35,
  ignoreLocation: true,
};

export function filterIntegrations(
  integrations: UnifiedIntegration[],
  selectedFilter: MarketplaceFilter,
  searchQuery: string
): UnifiedIntegration[] {
  const trimmed = searchQuery.trim();

  if (trimmed.length > 0) {
    const visibleIntegrations = integrations.filter((i) => hasAnyVisibleTag(i));
    const fuse = new Fuse(visibleIntegrations, FUSE_OPTIONS);
    return fuse.search(trimmed).map((result) => result.item);
  }

  if (selectedFilter === MARKETPLACE_FILTER_FEATURED) {
    return integrations.filter((i) => i.featured);
  }
  if (selectedFilter === MARKETPLACE_FILTER_ALL) {
    return integrations.filter((i) => hasAnyVisibleTag(i));
  }
  return integrations.filter((i) => matchesFilter(i, selectedFilter));
}

export function sortIntegrations(
  integrations: UnifiedIntegration[],
  selectedFilter: MarketplaceFilter
): UnifiedIntegration[] {
  return [...integrations].sort((a, b) => {
    if (selectedFilter === MARKETPLACE_FILTER_FEATURED) {
      const aIsAgency = a.category === IntegrationTag.AgencySystems;
      const bIsAgency = b.category === IntegrationTag.AgencySystems;
      if (aIsAgency && !bIsAgency) return -1;
      if (!aIsAgency && bIsAgency) return 1;
    }
    if (a.connected && !b.connected) return -1;
    if (!a.connected && b.connected) return 1;
    const aSupported = a.status === IntegrationListingStatus.Supported;
    const bSupported = b.status === IntegrationListingStatus.Supported;
    if (aSupported && !bSupported) return -1;
    if (!aSupported && bSupported) return 1;
    return (b.votes ?? 0) - (a.votes ?? 0);
  });
}

export async function submitVoteOrRequest(
  req: { integrationId?: string; integrationName?: string; useCase: string; urgency: VoteUrgency },
  isNewRequest: boolean,
  setIntegrations: React.Dispatch<React.SetStateAction<UnifiedIntegration[]>>
) {
  if (isNewRequest && req.integrationName) {
    const result = await requestNewIntegration({
      name: req.integrationName,
      useCase: req.useCase,
      urgency: req.urgency,
    });
    if (!result.success) throw new Error(result.error);
    return;
  }
  if (req.integrationId) {
    const result = await voteForIntegration({
      integrationId: req.integrationId,
      useCase: req.useCase,
      urgency: req.urgency,
    });
    if (!result.success) throw new Error(result.error);
    setIntegrations((prev) =>
      prev.map((i) => (i.id === req.integrationId ? { ...i, votes: (i.votes ?? 0) + 1 } : i))
    );
  }
}
